import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTheme, tokensToCssVars } from '@evi/skins';
import { customerPayLogo, getCustomerPay, getFreightPay, startCustomerPay, startFreightPay, usd } from '@/lib/customerPay';
import { cadencePriceLine } from '@/lib/planPrices';
import jobs from './PartnerJobs.module.css';
import styles from '../partner-quotes/Quotes.module.css';

/** The page's own light (default) or dark tokens, so the customer never takes a visitor's Admin theme. */
function useScopedMode() {
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const vars = useMemo(() => Object.fromEntries(Object.entries(tokensToCssVars(getTheme('default').variants[mode].tokens)).filter(([, v]) => v !== '')), [mode]);
  return { mode, setMode, vars: vars as React.CSSProperties };
}
function Header({ name, logo, mode, setMode }: { name: string; logo: string | null; mode: 'light' | 'dark'; setMode: (m: 'light' | 'dark') => void }) {
  return <header className={styles.bar}><div className={styles.barInner}>
    <div className={styles.brand}>{logo && <img className={styles.logo} src={logo} alt={`${name} logo`} />}<div className={jobs.strong} style={{ fontSize: 18 }}>{name}</div></div>
    <div className={styles.network}>Charging network by<strong>EV <span>INITIATIVE</span></strong></div>
    <button type="button" className={styles.modeToggle} onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}>{mode === 'light' ? 'Dark' : 'Light'}</button>
  </div></header>;
}
/** Pay: a fresh Stripe Checkout Session on each click (the emailed link never expires). */
function PayButton({ label, start }: { label: string; start: () => Promise<string> }) {
  const [busy, setBusy] = useState(false), [message, setMessage] = useState('');
  return <>
    <button type="button" className={styles.cta} disabled={busy} onClick={() => { setBusy(true); setMessage('');
      void start().then(url => window.location.assign(url), (err: unknown) => { setMessage(err instanceof Error ? err.message : 'The payment page did not open.'); setBusy(false); }); }}>
      {busy ? 'Opening the secure payment page' : label}</button>
    <p className={jobs.message} role="status">{message}</p>
  </>;
}

/**
 * The customer's order from EV Initiative (Warren, ruling A, 21 October 2026): the hardware at MSRP, the freight as one
 * line, the network plan billed only from go-live, and Pay, by US bank account or card. No sign-in; light by default.
 */
export function CustomerPayPage({ token }: { token: string }) {
  const paidReturn = new URLSearchParams(window.location.search).get('paid') === '1';
  const q = useQuery({ queryKey: ['customer-pay', token], queryFn: () => getCustomerPay(token), retry: false, refetchOnWindowFocus: false });
  const { mode, setMode, vars } = useScopedMode();
  useEffect(() => { document.title = q.data ? `Your order from ${q.data.partner.name}` : 'Your EV charger order'; }, [q.data]);
  const shell = (children: React.ReactNode) => <div className={styles.customer} style={vars} data-mode={mode}>{children}</div>;
  if (q.error) return shell(<main className={styles.customerMain}><h1 className={styles.hero}>Order not found</h1>
    <p className={jobs.lead}>This link does not open an order. Check the link in your email, or ask your contractor to send it again.</p></main>);
  if (!q.data) return shell(<main className={styles.customerMain}><p className={jobs.message}>Loading your order.</p></main>);
  const v = q.data, site = [v.site.street, v.site.city, v.site.state].filter(Boolean).join(', ');
  const open = v.state === 'payable' || v.state === 'failed';
  // The plan's price from its own Stripe Price, or "EV Initiative confirms the price" (Warren, 21 October 2026, night).
  const per = v.network ? cadencePriceLine(v.network.per_charger_cents, v.network.cadence, usd) : null;
  return shell(<>
    <Header name={v.partner.name} logo={v.partner.has_logo ? customerPayLogo(token) : null} mode={mode} setMode={setMode} />
    <main className={styles.customerMain}>
      <div><div className={jobs.kicker}>Your EV charger order</div>
        <h1 className={styles.hero}>{v.customer.business_name || v.customer.contact_name || 'Your site'}</h1>
        <div className={jobs.lead} style={{ fontSize: 16 }}>{[site, `From ${v.partner.name}, supplied by EV Initiative`].filter(Boolean).join(' · ')}</div></div>
      {paidReturn && open && <p className={styles.accepted} role="status">Thank you. Your payment is on its way to us; this page shows it within a minute.</p>}
      {v.message && <p className={v.state === 'paid' || v.state === 'clearing' ? styles.accepted : jobs.problems} role="status">{v.message}</p>}
      <div className={jobs.layout}>
        <div className={jobs.main}>
          <section className={jobs.card}>
            <h2 style={{ margin: 0, fontSize: 20 }}>Your order</h2>
            <div className={jobs.tableWrap}><table className={styles.items}><tbody>
              {v.lines.map((l, i) => <tr key={i}><th scope="row">{l.quantity} × {l.name}</th><td>{usd(l.total_cents)}</td></tr>)}
              {v.freight && <tr><th scope="row">{v.freight.line}{v.freight.note && <div>{v.freight.note}</div>}</th><td>{v.freight.price_cents != null ? usd(v.freight.price_cents) : 'To follow'}</td></tr>}
              <tr className={styles.totalRow}><th scope="row">Total today</th><td>{usd(v.total_cents)}</td></tr>
            </tbody></table></div>
            {v.tax_note && <p className={styles.fine}>{v.tax_note}</p>}
          </section>
          {v.network && <section className={jobs.card}>
            <h2 style={{ margin: 0, fontSize: 20 }}>EV Initiative network</h2>
            <p className={styles.quoteText}>{v.network.chargers} charger{v.network.chargers === 1 ? '' : 's'}{per ? `: ${per}` : ''}. {v.network.line}</p>
          </section>}
          <section className={jobs.card}>
            <h2 style={{ margin: 0, fontSize: 20 }}>Your installation</h2>
            <p className={styles.quoteText}>{v.labour}</p>
          </section>
        </div>
        <aside className={jobs.aside}>
          <section className={jobs.card}>
            <div className={jobs.sub}>To pay today</div>
            <div className={styles.big}>{usd(v.total_cents)}</div>
            <div className={jobs.sub}>{v.methods.includes('us_bank_account') ? 'By US bank account or card. A bank payment takes about 4 business days to clear.' : 'By card.'}
              {v.po_number ? ' Add your own PO number at checkout.' : ''}</div>
            {open && <PayButton label={v.state === 'failed' ? 'Pay again' : 'Review and pay'} start={() => startCustomerPay(token)} />}
            {v.partner.email && <a className={jobs.ghost} href={`mailto:${v.partner.email}?subject=${encodeURIComponent('Question about my EV charger order')}`}>Ask {v.partner.name} a question</a>}
          </section>
          <section className={jobs.card}>
            <div className={jobs.strong}>Your contractor</div>
            <div>{v.partner.name}</div>
            {v.partner.email && <div className={jobs.sub}>{v.partner.email}</div>}{v.partner.phone && <div className={jobs.sub}>{v.partner.phone}</div>}
          </section>
        </aside>
      </div>
      <p className={styles.fine}>EV Initiative sells and ships the hardware and runs the charging network. Payments are processed securely by Stripe.</p>
    </main>
  </>);
}

/** A freight quote by hand, paid on EVI's page (Warren, 21 October 2026): a fresh Checkout Session on each click. */
export function FreightPayPage({ token }: { token: string }) {
  const paidReturn = new URLSearchParams(window.location.search).get('paid') === '1';
  const q = useQuery({ queryKey: ['freight-pay', token], queryFn: () => getFreightPay(token), retry: false, refetchOnWindowFocus: false });
  const { mode, setMode, vars } = useScopedMode();
  useEffect(() => { document.title = 'Pay the freight'; }, []);
  const shell = (children: React.ReactNode) => <div className={styles.customer} style={vars} data-mode={mode}>{children}</div>;
  if (q.error) return shell(<main className={styles.customerMain}><h1 className={styles.hero}>Freight quote not found</h1>
    <p className={jobs.lead}>This link does not open a freight quote. Check the link in your email.</p></main>);
  if (!q.data) return shell(<main className={styles.customerMain}><p className={jobs.message}>Loading the freight quote.</p></main>);
  const v = q.data;
  return shell(<>
    <Header name={v.partner.name} logo={null} mode={mode} setMode={setMode} />
    <main className={styles.customerMain}>
      <div><div className={jobs.kicker}>Freight</div><h1 className={styles.hero}>{v.title || `Order ${v.order_number}`}</h1>
        <div className={jobs.lead} style={{ fontSize: 16 }}>{[`Order ${v.order_number}`, v.ship_to ? `Shipping to ${v.ship_to}` : null].filter(Boolean).join(' · ')}</div></div>
      {v.state === 'paid' && <p className={styles.accepted} role="status">The freight is paid. Thank you.</p>}
      {paidReturn && v.state === 'payable' && <p className={styles.accepted} role="status">Thank you. Your payment shows here within a minute.</p>}
      {v.state === 'closed' && <p className={jobs.problems} role="status">This freight quote is not open for payment. Contact your contractor.</p>}
      <section className={jobs.card}>
        <div className={jobs.tableWrap}><table className={styles.items}><tbody>
          <tr className={styles.totalRow}><th scope="row">{v.line}</th><td>{v.price_cents != null ? usd(v.price_cents) : ''}</td></tr>
        </tbody></table></div>
        {v.notes && <p className={styles.quoteText}>{v.notes}</p>}
        {v.state === 'payable' && <PayButton label="Pay the freight" start={() => startFreightPay(token)} />}
        <p className={styles.fine}>Nothing ships until the freight is paid. Payments are processed securely by Stripe.</p>
      </section>
    </main>
  </>);
}
